import React, { useState, Children } from 'react';
import { ModernHeader } from '../components/ModernHeader';
import { Footer } from '../components/Footer';
import { FileText, ChevronRight, ChevronDown, Folder, ExternalLink, Info } from 'lucide-react';
// Type definition for the hierarchical data
type ChartNode = {
  label: string;
  url?: string;
  pdfUrl?: string;
  pdfLabel?: string;
  children?: ChartNode[];
  note?: string;
};
// Data derived from the user's HTML content
const chartData: ChartNode[] = [{
  label: 'Miscellaneous',
  url: '18m7/miscellaneous.html'
}, {
  label: 'Agricultural Bearings',
  url: '18m7/agricultural-bearings.html'
}, {
  label: 'Ball Bearings',
  children: [{
    label: '1600-Series Bearings',
    url: '18m7/single-row-ball-bearings/1600-series.html',
    pdfUrl: 'uploaded/files/1600%20Model%20Chart.pdf',
    pdfLabel: '1600 Model Chart.pdf'
  }, {
    label: '6000-Series Bearings',
    url: '18m7/single-row-ball-bearings/6000-series.html',
    pdfUrl: 'uploaded/files/6000-Model%20Chart.pdf',
    pdfLabel: '6000-Model Chart.pdf'
  }, {
    label: '6200-Series Bearings',
    url: '18m7/single-row-ball-bearings/6200-series.html',
    pdfUrl: 'uploaded/files/6200-Model%20Chart.pdf',
    pdfLabel: '6200-Model Chart.pdf',
    children: [{
      label: 'Stainless Steel',
      url: '18m7/single-row-ball-bearings/6200-series/stainless-steel.html'
    }]
  }, {
    label: '6300-Series Bearings',
    url: '18m7/single-row-ball-bearings/6300-series.html',
    pdfUrl: 'uploaded/files/6300-Model%20Chart.pdf',
    pdfLabel: '6300-Model Chart.pdf'
  }, {
    label: '6800-Series Bearings',
    url: '18m7/single-row-ball-bearings/6800-series.html',
    pdfUrl: 'uploaded/files/6800-Model%20Chart.pdf',
    pdfLabel: '6800-Model Chart.pdf'
  }, {
    label: '6900-Series Bearings',
    url: '18m7/single-row-ball-bearings/6900-series.html',
    pdfUrl: 'uploaded/files/6900-Model%20Chart.pdf',
    pdfLabel: '6900-Model Chart.pdf'
  }, {
    label: '5200-Series Bearings (Double Row)',
    url: '18m7/double-row-ball-bearings/5200-series.html',
    pdfUrl: 'uploaded/files/5200-Model%20Chart.pdf',
    pdfLabel: '5200-Model Chart.pdf'
  }, {
    label: '5300-Series Bearings (Double Row)',
    url: '18m7/double-row-ball-bearings/5300-series.html',
    pdfUrl: 'uploaded/files/5300-Model%20Chart.pdf',
    pdfLabel: '5300-Model Chart.pdf'
  }, {
    label: 'R-Series Bearings',
    url: '18m7/r-series-bearings.html',
    pdfUrl: 'uploaded/files/R-Series-Model%20Chart.pdf',
    pdfLabel: 'R-Series-Model Chart.pdf',
    children: [{
      label: 'Stainless Steel',
      url: '18m7/r-series-bearings/stainless-steel.html'
    }]
  }, {
    label: 'Flanged Bearings',
    url: '18m7/flanged-bearings.html'
  }, {
    label: 'Miniature Bearings',
    url: '18m7/miniature-bearings.html',
    pdfUrl: 'uploaded/files/MetricMiniature-Model%20Chart.pdf',
    pdfLabel: 'MetricMiniature-Model Chart.pdf'
  }, {
    label: 'SKF Brand Bearings',
    children: [{
      label: '6000 SKF Bearings',
      url: '18m7/skf-brand-bearings/6000-skf-bearings.html'
    }, {
      label: '6200 SKF Bearings',
      url: '18m7/skf-brand-bearings/6200-skf-bearings.html'
    }, {
      label: '6300 SKF Bearings',
      url: '18m7/skf-brand-bearings/6300-skf-bearings.html'
    }, {
      label: '6800 SKF Bearings',
      url: '18m7/skf-brand-bearings/6800-skf-bearings.html'
    }, {
      label: '6900 SKF Bearings',
      url: '18m7/skf-brand-bearings/6900-skf-bearings.html'
    }]
  }]
}, {
  label: 'Pillow Block Bearings',
  children: [{
    label: 'UCP Series Steel Units',
    url: '18m7/pillow-block-bearing-units/ucp-series-steel-units.html',
    pdfUrl: 'uploaded/files/more%20info%20all%20pillow%20blocks.pdf',
    pdfLabel: 'more info all pillow blocks.pdf'
  }, {
    label: 'HCP Series Steel Units',
    url: '18m7/pillow-block-bearing-units/hcp-series-steel-units.html'
  }, {
    label: 'Stainless Steel UPC Units',
    url: '18m7/pillow-block-bearing-units/stainless-steel-upc-units.html'
  }, {
    label: 'Thermoplastic UPC Bearing Units',
    url: '18m7/pillow-block-bearing-units/thermoplastic-upc-bearing-units.html'
  }, {
    label: 'Pressed Steel Pillow Block Unit',
    url: '18m7/pillow-block-bearing-units/pressed-steel-pillow-block-unit.html'
  }]
}, {
  label: '2 Bolt Flange Bearing Units',
  pdfUrl: 'uploaded/files/More%20Info%20all%20UCFL.pdf',
  pdfLabel: 'More Info all UCFL.pdf',
  children: [{
    label: 'Steel 2-Bolt Flange Units',
    url: '18m7/2-bolt-flange-unit/steel-2-bolt-flange-units.html'
  }, {
    label: 'Stainless Steel 2-Bolt Flange Units',
    url: '18m7/2-bolt-flange-unit/stainless-steel-2-bolt-flange-units.html'
  }, {
    label: 'Thermoplastic 2-Bolt Flange Units',
    url: '18m7/2-bolt-flange-unit/thermoplastic-2-bolt-flange-units.html'
  }, {
    label: 'Pressed Steel 2-Bolt Flange Units',
    url: '18m7/2-bolt-flange-unit/pressed-steel-2-bolt-flange-units.html',
    children: [{
      label: 'SAPFL-2 Bolt Stamped Steel with Eccentric Locking Collar',
      url: '18m7/2-bolt-flange-unit/pressed-steel-2-bolt-flange-units/sapfl-2-bolt-stamped-steel-with-eccentric-locking-collar.html'
    }, {
      label: 'SBPFL-2-Bolt Pressed Steel Units with Set Screws',
      url: '18m7/2-bolt-flange-unit/pressed-steel-2-bolt-flange-units/sbpfl-2-bolt-pressed-steel-units-with-set-screws.html'
    }]
  }]
}, {
  label: '4 Bolt Flange Bearing Units',
  pdfUrl: 'uploaded/files/More%20Info%20UCF.pdf',
  pdfLabel: 'More Info UCF.pdf',
  children: [{
    label: 'Steel 4-Bolt Flange Units',
    url: '18m7/4-bolt-flange-units/steel-4-bolt-flange-units.html'
  }, {
    label: 'Stainless Steel 4 Bolt Flange Units',
    url: '18m7/4-bolt-flange-units/stainless-steel-4-bolt-flange-units.html'
  }, {
    label: 'Thermoplastic 4-Bolt Flange Units',
    url: '18m7/4-bolt-flange-units/thermoplastic-4-bolt-flange-units.html'
  }]
}, {
  label: 'Round Flanged Cartridge Units UCFC Series',
  url: '18m7/round-flanged-cartridge-units-ucfc-series.html'
}, {
  label: 'Take Up Bearings',
  url: '18m7/take-up-bearings.html'
}, {
  label: 'Tapped Base Bearings',
  url: '18m7/tapped-base-bearings.html'
}, {
  label: 'Insert Bearings',
  children: [{
    label: 'CSA Series',
    url: '18m7/insert-bearings/csa-series.html'
  }, {
    label: 'SA Series',
    url: '18m7/insert-bearings/sa-series.html'
  }, {
    label: 'SB Series',
    url: '18m7/insert-bearings/sb-series.html'
  }, {
    label: 'SER Series',
    url: '18m7/insert-bearings/ser-series.html'
  }, {
    label: 'UC Series',
    url: '18m7/insert-bearings/uc-series.html',
    children: [{
      label: 'Stainless Steel'
    }]
  }]
}, {
  label: 'Plummer Block Bearing Units',
  url: '18m7/plummer-block-bearing-units.html'
}, {
  label: 'Bushings',
  url: '18m7/bushings.html'
}, {
  label: 'Chain',
  children: [{
    label: '100 Chain',
    url: '18m7/chain/100-chain.html'
  }, {
    label: '120 Chain',
    url: '18m7/chain/120-chain.html'
  }, {
    label: '140 Chain',
    url: '18m7/chain/140-chain.html'
  }, {
    label: '25 Chain',
    url: '18m7/chain/25-chain.html'
  }, {
    label: '35 Chain',
    url: '18m7/chain/35-chain.html'
  }, {
    label: '40 Chain',
    url: '18m7/chain/40-chain.html'
  }, {
    label: '41 Chain',
    url: '18m7/chain/41-chain.html'
  }, {
    label: '420 Chain',
    url: '18m7/chain/420-chain.html'
  }, {
    label: '50 Chain',
    url: '18m7/chain/50-chain.html'
  }, {
    label: '60 Chain',
    url: '18m7/chain/60-chain.html'
  }, {
    label: '80 Chain',
    url: '18m7/chain/80-chain.html'
  }]
}, {
  label: 'Electric Motors',
  url: '18m7/electric-motors.html' // Inferred from context or just a label
}, {
  label: 'Go-Kart Bearings',
  url: '18m7/go-kart-bearings.html'
}, {
  label: 'HEIM Joints / Rod Ends',
  url: '18m7/heim-joints-rod-ends.html'
}, {
  label: 'Key Stock',
  url: '18m7/key-stock.html'
}, {
  label: 'Lawn Mower Bearings & Belts',
  children: [{
    label: 'Lawn Mower Bearings',
    url: '18m7/lawn-mower-bearings-belts/lawn-mower-bearings.html'
  }, {
    label: 'Lawn Mower Belts',
    url: '18m7/lawn-mower-bearings-belts/lawn-mower-belts.html'
  }]
}, {
  label: 'Motorcycle Wheel Bearings',
  url: '18m7/motorcycle-wheel-bearings.html'
}, {
  label: 'Neptune Washer Bearings',
  url: '18m7/neptune-washer-bearings.html'
}, {
  label: 'Pulleys/Sheaves',
  children: [{
    label: 'Electric Motor Pulleys',
    url: '18m7/pulleys-sheaves/electric-motor-pulleys.html'
  }]
}, {
  label: 'Shaft Collars',
  children: [{
    label: 'Solid Shaft Collars',
    children: [{
      label: 'Solid Zinc Plated',
      url: '18m7/shaft-collars/solid-shaft-collars/solid-zinc-plated.html'
    }, {
      label: 'Stainless Steel Solid',
      url: '18m7/shaft-collars/solid-shaft-collars/stainless-steel-solid.html'
    }]
  }, {
    label: 'Split Shaft Collars',
    children: [{
      label: 'Black Oxide Steel',
      url: '18m7/shaft-collars/double-split-collar/black-oxide-steel.html'
    }, {
      label: 'Stainless Steel',
      url: '18m7/shaft-collars/double-split-collar/stainless-steel.html'
    }]
  }]
}, {
  label: 'Snow Blower/Thrower Bearings & Belts',
  children: [{
    label: 'Snow Blower/Thrower Bearings',
    url: '18m7/snow-blower-thrower-bearings-belts/snow-blower-thrower-bearings.html'
  }, {
    label: 'Snow Blower/Thrower Belts',
    url: '18m7/snow-blower-thrower-bearings-belts/snow-blower-thrower-belts.html'
  }]
}, {
  label: 'Snowmobile Bearings',
  url: '18m7/snowmobile-bearings.html'
}, {
  label: 'Sprockets',
  children: [{
    label: '#25 Sprockets',
    url: '18m7/sprockets/25-sprockets.html'
  }, {
    label: '#35 Sprockets',
    url: '13m7/sprockets/35-sprockets.html'
  }, {
    label: '#40 Sprockets'
  }, {
    label: '#41 Sprockets',
    url: '18m7/sprockets/41-sprockets.html'
  }, {
    label: '#50 Sprockets',
    url: '18m7/sprockets/50-sprockets.html'
  }, {
    label: '#60 Sprockets',
    url: '18m7/sprockets/60-sprockets.html'
  }, {
    label: '#80 Sprockets',
    url: '18m7/sprockets/80-sprockets.html'
  }]
}, {
  label: 'Taper Roller Bearings',
  url: '18m7/taper-roller-bearings.html'
}, {
  label: 'Turf OEM Parts',
  url: '18m7/turf-oem-parts.html'
}, {
  label: 'V-Belts',
  url: '18m7/v-belts.html',
  children: [{
    label: 'Accu-Link',
    url: '18m7/v-belts/accu-link.html',
    children: [{
      label: 'Type A Accu-link',
      url: '18m7/v-belts/accu-link/type-a-accu-link.html'
    }, {
      label: 'Type B Accu-Link',
      url: '18m7/v-belts/accu-link/type-b-accu-link.html'
    }, {
      label: 'Type C Accu-Link',
      url: '18m7/v-belts/accu-link/type-c-accu-link.html'
    }, {
      label: 'Type 3L Accu-Link',
      url: '18m7/v-belts/accu-link/type-3l-accu-link.html'
    }]
  }, {
    label: 'Type A',
    url: '18m7/v-belts/type-a.html',
    children: [{
      label: 'Type A V-Belts',
      url: '13m7/v-belts/type-a/type-a-v-belts.html'
    }, {
      label: 'Multi Banded A V-Belts',
      url: '18m7/v-belts/type-a/multi-banded-a-v-belts.html'
    }, {
      label: 'Cogged/Notched A V-Belts',
      url: '18m7/v-belts/type-a/cogged-notched-a-v-belts.html'
    }]
  }, {
    label: 'Type B',
    url: '18m7/v-belts/type-b.html',
    children: [{
      label: 'Type B V-Belts',
      url: '18m7/v-belts/type-b/type-b-v-belts.html'
    }, {
      label: 'Multi Banded B V-Belts',
      url: '18m7/v-belts/type-b/multi-banded-b-v-belts.html'
    }, {
      label: 'Cogged/Notched B V-Belts',
      url: '18m7/v-belts/type-b/cogged-notched-b-v-belts.html'
    }]
  }, {
    label: 'Lawn & Garden',
    url: '18m7/v-belts/lawn-garden.html',
    children: [{
      label: 'Type A Lawn & Garden',
      url: '18m7/v-belts/lawn-garden/type-a-lawn-garden.html'
    }, {
      label: 'Type B Lawn & Garden',
      url: '18m7/v-belts/lawn-garden/type-b-lawn-garden.html'
    }]
  }]
}];
// Recursive component to render the tree
const ChartItem = ({
  node,
  depth = 0
}: {
  node: ChartNode;
  depth?: number;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isRoot = depth === 0;
  return <div className={`
      ${isRoot ? 'mb-4' : 'ml-4 sm:ml-8 mt-2'}
      ${isRoot ? 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden' : ''}
    `} data-id="element-701">
      {/* Node Header */}
      <div className={`
        flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4
        ${isRoot ? 'p-4 bg-gray-50 border-b border-gray-100' : ''}
      `} data-id="element-702">
        {/* Main Link/Label */}
        <div className="flex items-center gap-2 flex-1" data-id="element-703">
          {hasChildren && <button onClick={() => setIsOpen(!isOpen)} className="p-1 hover:bg-gray-200 rounded text-gray-500 transition-colors" aria-label={isOpen ? 'Collapse' : 'Expand'} data-id="element-704">
              {isOpen ? <ChevronDown className="w-4 h-4" data-id="element-705" /> : <ChevronRight className="w-4 h-4" data-id="element-706" />}
            </button>}
          {!hasChildren && <div className="w-6" data-id="element-707" />} {/* Spacer for alignment */}
          {node.url ? <a href={node.url} className={`
                font-medium hover:underline flex items-center gap-2
                ${isRoot ? 'text-lg text-gray-900' : 'text-blue-700 hover:text-blue-900'}
              `} data-id="element-708">
              {isRoot && <Folder className="w-5 h-5 text-red-700" data-id="element-709" />}
              {node.label}
            </a> : <span className={`font-medium ${isRoot ? 'text-lg text-gray-900 flex items-center gap-2' : 'text-gray-700'}`} data-id="element-710">
              {isRoot && <Folder className="w-5 h-5 text-red-700" data-id="element-711" />}
              {node.label}
            </span>}
        </div>

        {/* PDF Download Link */}
        {node.pdfUrl && <a href={node.pdfUrl} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 rounded text-sm font-medium transition-colors border border-red-200 w-fit" target="_blank" rel="noopener noreferrer" data-id="element-712">
            <FileText className="w-4 h-4" data-id="element-713" />
            <span data-id="element-714">{node.pdfLabel || 'Download Chart'}</span>
          </a>}
      </div>

      {/* Children */}
      {hasChildren && isOpen && <div className={`
          ${isRoot ? 'p-4 pt-2' : 'border-l-2 border-gray-100 pl-2'}
        `} data-id="element-715">
          {node.children!.map((child, index) => <ChartItem key={`${child.label}-${index}`} node={child} depth={depth + 1} data-id="element-716" />)}
        </div>}
    </div>;
};
export function ModelChartsPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-717">
      <ModernHeader data-id="element-718" />

      {/* Page Header Banner */}
      <section className="bg-gray-800 py-12" data-id="element-719">
        <div className="max-w-7xl mx-auto px-4" data-id="element-720">
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center" data-id="element-721">
            Model Charts & Maps
          </h1>
          <p className="text-gray-300 text-center mt-2 max-w-2xl mx-auto" data-id="element-722">
            Navigate our site and download detailed model charts for bearings,
            belts, and components.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-12 md:py-16" data-id="element-723">
        <div className="max-w-5xl mx-auto px-4" data-id="element-724">
          {/* Legend / Info Box */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg shadow-sm mb-10" data-id="element-725">
            <div className="flex items-start gap-4" data-id="element-726">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-727" />
              <div data-id="element-728">
                <h3 className="text-lg font-bold text-blue-900 mb-2" data-id="element-729">
                  To assist you in navigating our site, we have provided the
                  following map.
                </h3>
                <div className="grid sm:grid-cols-2 gap-4 mt-4" data-id="element-730">
                  <div className="bg-white p-3 rounded border border-blue-100 shadow-sm" data-id="element-731">
                    <span className="font-bold text-gray-900" data-id="element-732">ZZ</span>
                    <span className="text-gray-600 ml-2" data-id="element-733">
                      Means Metal Shields on 2 sides
                    </span>
                  </div>
                  <div className="bg-white p-3 rounded border border-blue-100 shadow-sm" data-id="element-734">
                    <span className="font-bold text-gray-900" data-id="element-735">2RS</span>
                    <span className="text-gray-600 ml-2" data-id="element-736">
                      Means Rubber Seals on 2 sides
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts List */}
          <div className="space-y-2" data-id="element-737">
            {chartData.map((node, index) => <ChartItem key={`${node.label}-${index}`} node={node} data-id="element-738" />)}
          </div>
        </div>
      </main>

      <Footer data-id="element-739" />
    </div>;
}